//store representing customers
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import api from '@/libs/axios';
import type { Customer } from '@/types/types'
import { useAlertStore, useUserStore, useCacheStore } from '@/stores'
import { useStoreSyncer } from '@/composables/Store-syncer';
import * as yup from 'yup'
import { sumField } from '@/libs/utils'
import router from '@/router';


const newCustomerSchema = yup.object({
  created_by: yup.string().defined(), // string
  name: yup.string(), //string
  companyName: yup.string(), //string
  invoiceName: yup.string().defined().required(), //string
  notes: yup.string(), //string
  email: yup.string().email().transform((val => {
    return val === '' || val === null ? "" : val;
  })), //string
  source: yup.string().notRequired()
})

const existingCustomerSchema = yup.object({
  id: yup.number().defined(), //number
  name: yup.string(), //string
  companyName: yup.string(), //string
  invoiceName: yup.string().defined().required(), //string
  notes: yup.string(), //string
  email: yup.string().email().transform((val => {
    return val === '' || val === null ? "" : val;
  })), //string
  source: yup
    .string()
    .notRequired()
    .transform((newVal) => (newVal === '' ? null : newVal)),
  archived: yup.boolean()
})

export const useCustomerStore = defineStore('customers', () => {
  //const cache = useCacheStore();
  const { fetchAll, update, remove, create, allItems, loading, error } = useStoreSyncer('/customers', 'customers'); 

  const allCustomers = computed(()=>{
    //return cache.getFromCache('customers') || [];
    return allItems.value;
  })

  //const loading = ref(true);

  const isLoading = computed(()=>{
    return loading.value;
  })
 


  /**
   * return in format for customer table
   */
  const tableData = computed(() =>{
    loading.value = true;
    //const data = cache.getFromCache('customers') || [];
    const mapped = allItems.value.map((cust) => {
    const projectCount = cust.projects.length;
    cust.projectCount = projectCount;
    const invoiceCount = cust.invoices.length;
    cust.invoiceCount = invoiceCount;
    const postechCount = cust.postechs.length;
    cust.postechCount = postechCount;
    //get total invoiced
    //const initialValue = 0;
    try {
      const totalInvoiced = sumField(cust.invoices, 'valueTotal')
      cust.totalInvoiced = parseFloat(totalInvoiced)
    } catch (error) {
      console.error('..trapped error on Customers-table.parseTableData, customer has no invoices..skipping')
    }
      return cust
    })
    loading.value = false;
    return mapped;
  })

  /**
   * Forced fetch from backend, reset cache of all objects
   */
  async function fetchAllCustomers(): Promise<Customer[]> {
    console.log('customer store fetching all customers from api or cache...')
    return await fetchAll();
  }

  /**
   * add a new customer from form, push router back
   */
  async function add(customer: Customer) {
    console.log('Customer store add()...')
    console.log('validating object : ' + JSON.stringify(customer))
    //loading.value = true;
    const userStore = useUserStore()
    const creator = userStore.currentEmail
    customer.created_by = creator
    try{
      const created = await create(customer, newCustomerSchema, 'customer');
      if(created){
        router.back();
      }
    }catch(err){
      console.error(".error on adding customer, err =" + err)
      console.log("..error state from store-syncer = " + error.value);

    }

  }

  /**
   * update and existing customer from form
   */
  async function updateCustomer(customer: Customer) {
    console.log('Customer store update()...')
    return await update(customer, existingCustomerSchema.omit(['id']), 'customer');
  }

  /**
   * delete existing customer by its id
   */
  async function removeCustomer(id: number) {
    console.log(`customer store.deleteCustomer(${id})...`)
    return await remove(id);
  }

  /**
   * get by id
   */
  async function getById(id: number) {
    console.log(`customer store.getById(${id})...`)
    try {
      const found = allCustomers.value.find((cust) => {
        //console.log("....cust : " + cust.id + " ...id : " + id);
        return cust.id === id
      })
      return found;
    } catch (error) {
      console.error(`error finding custommer ${id} in store..` + error);
    }
  }

  /**
   * merge one customer into another
   * This will replace all customer ids within the projects, postech, invoices
   * tables from the old customer with the new customer
   */
  async function mergeCustomerInto(customerOldId: number, customerNewId: number) {
    console.log(`customer store mergeCustomerInto(${customerOldId}, ${customerNewId})`)
    // console.log("validating object : " + JSON.stringify(customer));
    //try form validation
    const alert = useAlertStore()
    //separate id
    api
      .post(`/customers/merge/${customerOldId}/into/${customerNewId}`)
      .then((res) => {
        alert.success(res.data.message + ', deleted : ' + JSON.stringify(res.data.data.customer_id))
        setTimeout(() => {
          fetchAllCustomers()
          alert.clear()
          // router.back();
        }, 3000)
      })
      .catch((error) => {
        if (error.response) {
          alert.error(error.response.data)
        } else if (error.request) {
          alert.error(error.request)
        }
      })
  }

/**
   * fetch the table data from the view in the DB
   */
  async function fetchTableData(){
    console.log("customers store fetchTableData()..");
    loading.value = true;
    const response = await api.get('/customers/view');
    loading.value = false;
    console.log("...response = " + response.data.status + " : " + response.data.message);
    return response.data.data.customers;
  }

  /**
   * fetch from api
   */
  async function fetchById(id : number){
    console.log(`customer store fetchById(${id})...`);
    loading.value = true;
    const response = await api.get(`/customers/${id}`);
    loading.value = false;
    console.log("...response = " + response.data.status + " : " + response.data.message);
    return response.data.data.customer;
  }

  return { 
    allCustomers, 
    fetchAllCustomers, 
    add, 
    updateCustomer, 
    removeCustomer, 
    getById, 
    mergeCustomerInto, 
    isLoading, 
    loading, 
    error, 
    tableData,
    fetchTableData,
  fetchById }
})
